import React from 'react'
import { UserService } from '@duck/api-definitions'
import { getRoleBadgeColor, formatRole, getStatusBadgeColor, formatStatus, formatAccessGroups, type Column } from './utils'
import { UsersTableSkeleton } from './UsersTableSkeleton'
import { RelativeDateTime } from '../datetime/RelativeDateTime'
import { useTar } from '@/modules'

interface UsersTableProps {
  users: UserService.User[]
  totalUsers: number
  visibleColumns: Column[]
  sortField: keyof UserService.User
  sortDirection: 'asc' | 'desc'
  onSort: (field: keyof UserService.User) => void
  onUserClick?: (user: UserService.User) => void
  loadingMode?: boolean
}

export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  totalUsers,
  visibleColumns,
  sortField,
  sortDirection,
  onSort,
  onUserClick,
  loadingMode = false,
}) => {
  const { t } = useTar()

  const cellRenderers: Partial<Record<keyof UserService.User, (user: UserService.User) => React.ReactNode>> = {
    id: (user) => (
      <span className="text-xs font-mono text-muted-foreground">
        {user.id}
      </span>
    ),
    email: (user) => (
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
            <span className="text-sm font-medium text-accent">
              {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
            </span>
          </div>
        </div>
        <div className="ms-4">
          <p className="text-sm font-medium text-foreground">
            {user.firstName} {user.lastName}
          </p>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
      </div>
    ),
    firstName: (user) => (
      <p className="text-sm text-foreground">
        {user.firstName}
      </p>
    ),
    lastName: (user) => (
      <p className="text-sm text-foreground">
        {user.lastName}
      </p>
    ),
    role: (user) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadgeColor(user.role)}`}>
        {formatRole(user.role)}
      </span>
    ),
    accessGroups: (user) => (
      <span className="text-sm text-muted-foreground">
        {formatAccessGroups(user.accessGroups)}
      </span>
    ),
    isActive: (user) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(user.isActive, user.isVerified)}`}>
        {t(formatStatus(user.isActive, user.isVerified))}
      </span>
    ),
    isVerified: (user) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.isVerified
        ? 'bg-success/10 text-success border border-success/20'
        : 'bg-warning/10 text-warning border border-warning/20'
        }`}>
        {user.isVerified ? t('common.verified') : t('common.unverified')}
      </span>
    ),
    organizationId: (user) => (
      <span className="text-sm text-muted-foreground">
        {user.organizationId || t('common.na')}
      </span>
    ),
    invitationId: (user) => (
      <span className="text-sm text-muted-foreground">
        {user.invitationId || t('common.notApplicable')}
      </span>
    ),
    createdAt: (user) => (
      <RelativeDateTime
        isoDateString={user.createdAt}
        className="text-sm text-muted-foreground"
      />
    ),
    updatedAt: (user) => (
      <RelativeDateTime
        isoDateString={user.updatedAt}
        className="text-sm text-muted-foreground"
      />
    ),
    lastLoginAt: (user) => (
      <RelativeDateTime
        isoDateString={user.lastLoginAt || ''}
        className="text-sm text-muted-foreground"
        fallbackText={t('common.never')}
      />
    ),
    avatar: (user) => (
      <span className="text-sm text-muted-foreground">
        {user.avatar ? t('common.hasAvatar') : t('common.noAvatar')}
      </span>
    ),
    notes: (user) => (
      <span className="text-sm text-muted-foreground max-w-xs truncate block" title={user.notes || ''}>
        {user.notes || t('common.none')}
      </span>
    ),
    personalInfo: (user) => (
      <span className="text-sm text-muted-foreground">
        {user.personalInfo?.yearsOfXP} {user.personalInfo?.profession}
      </span>
    ),
    personId: (user) => (
      <span className="text-sm text-muted-foreground">
        {user.personId || t('common.na')}
      </span>
    ),
  }

  const renderCellContent = (user: UserService.User, columnKey: keyof UserService.User) => {
    const renderer = cellRenderers[columnKey]
    return renderer ? renderer(user) : null
  }

  return (
    <div className="bg-card shadow overflow-hidden rounded-md sm:rounded-md border border-border">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-foreground">
          {t('users.allUsers')}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          {loadingMode ? t('common.loading') : t('users.usersShown', { usersShown: users.length, totalUsers: totalUsers })}
        </p>
      </div>

      <div className="overflow-x-auto overflow-y-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-secondary/5">
            <tr>
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider"
                >
                  {column.sortable ? (
                    <button
                      onClick={() => onSort(column.key as keyof UserService.User)}
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      <span>{t(column.label)}</span>
                      {sortField === column.key && (
                        <span className="text-accent">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  ) : (
                    t(column.label)
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {loadingMode ? (
              <UsersTableSkeleton visibleColumns={visibleColumns} rowCount={8} />
            ) : (
              users.map((user) => (
                <tr
                  key={user.id}
                  className={`hover:bg-secondary/5 ${onUserClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onUserClick?.(user)}
                >
                  {visibleColumns.map((column) => (
                    <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                      {renderCellContent(user, column.key)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!loadingMode && users.length === 0 && (
        <div className="text-center py-12">
          <p className="text-sm text-muted-foreground">{t('users.noUsers')}</p>
        </div>
      )}
    </div>
  )
}
